import {
  createMemoryInputSchema,
  mediaAssetSchema,
  memorySchema,
  type MediaAsset,
  type Memory,
  type MemoryWithMedia
} from "@memory-universe/domain";
import type { CreateMemoryCommand, MemoryRepository, RecentlyDeletedItem } from "./memoryRepository";
import { readLocalMediaPreviewUrl } from "../../../shared/platform/localOriginalMedia";

const DATABASE_NAME = "memory-universe-local";
const DATABASE_VERSION = 1;
const MEMORIES_STORE = "memories";
const MEDIA_STORE = "media";
const LOCAL_USER_ID = "local-user";

interface StoredMedia {
  metadata: MediaAsset;
  blob?: Blob;
  nativeCopy?: boolean;
  deletedAt?: string | null;
}

let databasePromise: Promise<IDBDatabase> | undefined;

function openDatabase() {
  databasePromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(MEMORIES_STORE)) {
        database.createObjectStore(MEMORIES_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(MEDIA_STORE)) {
        database.createObjectStore(MEDIA_STORE, { keyPath: "metadata.id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("无法打开本地记忆数据库"));
    request.onblocked = () => reject(new Error("本地数据库正在被另一个页面占用"));
  });

  return databasePromise;
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("本地数据库操作失败"));
  });
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("保存本地记忆失败"));
    transaction.onabort = () => reject(transaction.error ?? new Error("保存本地记忆已取消"));
  });
}

async function hydrateMemory(database: IDBDatabase, memory: Memory): Promise<MemoryWithMedia> {
  const transaction = database.transaction(MEDIA_STORE, "readonly");
  const done = transactionDone(transaction);
  const mediaStore = transaction.objectStore(MEDIA_STORE);
  const storedMedia = await Promise.all(
    memory.mediaIds.map((mediaId) => requestResult(mediaStore.get(mediaId) as IDBRequest<StoredMedia | undefined>))
  );
  await done;

  const media = (await Promise.all(storedMedia.map(async (stored) => {
    if (!stored || stored.deletedAt) {
      return null;
    }

    const metadata = mediaAssetSchema.parse(stored.metadata);
    const nativePreviewUrl = stored.nativeCopy ? await readLocalMediaPreviewUrl(metadata.id) : null;
    if (nativePreviewUrl) return { ...metadata, url: nativePreviewUrl };
    return stored.blob ? { ...metadata, url: URL.createObjectURL(stored.blob) } : null;
  }))).flatMap((item) => item ? [item] : []);

  return { memory, media };
}

class IndexedDbMemoryRepository implements MemoryRepository {
  async list(galaxyId: string) {
    const database = await openDatabase();
    const transaction = database.transaction(MEMORIES_STORE, "readonly");
    const done = transactionDone(transaction);
    const records = await requestResult(
      transaction.objectStore(MEMORIES_STORE).getAll() as IDBRequest<Memory[]>
    );
    await done;

    const memories = records
      .map((record) => memorySchema.parse(record))
      .filter((memory) => memory.deletedAt === null && memory.spaceId === galaxyId)
      .toSorted((left, right) => Date.parse(left.occurredAt) - Date.parse(right.occurredAt));

    return Promise.all(memories.map((memory) => hydrateMemory(database, memory)));
  }

  async get(memoryId: string) {
    const database = await openDatabase();
    const transaction = database.transaction(MEMORIES_STORE, "readonly");
    const done = transactionDone(transaction);
    const record = await requestResult(
      transaction.objectStore(MEMORIES_STORE).get(memoryId) as IDBRequest<Memory | undefined>
    );
    await done;

    if (!record) {
      return null;
    }

    const memory = memorySchema.parse(record);
    return memory.deletedAt === null ? hydrateMemory(database, memory) : null;
  }

  async create(command: CreateMemoryCommand) {
    const database = await openDatabase();
    const input = createMemoryInputSchema.parse(command.input);
    const now = new Date().toISOString();
    const memoryId = crypto.randomUUID();
    const storedMedia: StoredMedia[] = command.media.map((item) => {
      const { file } = item;
      const metadata = mediaAssetSchema.parse({
        id: item.id,
        memoryId,
        ownerId: LOCAL_USER_ID,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        createdAt: now,
        capturedAt: input.occurredAt,
        allowComments: true,
        allowDoodles: true
      });

      return item.nativeCopy ? { metadata, nativeCopy: true } : { metadata, blob: file };
    });

    const memory = memorySchema.parse({
      id: memoryId,
      spaceId: command.galaxyId,
      ...input,
      mediaIds: storedMedia.map((stored) => stored.metadata.id),
      createdBy: LOCAL_USER_ID,
      updatedBy: LOCAL_USER_ID,
      version: 1,
      createdAt: now,
      updatedAt: now,
      deletedAt: null
    });

    const transaction = database.transaction([MEMORIES_STORE, MEDIA_STORE], "readwrite");
    const done = transactionDone(transaction);
    transaction.objectStore(MEMORIES_STORE).add(memory);
    for (const media of storedMedia) {
      transaction.objectStore(MEDIA_STORE).add(media);
    }
    await done;

    return hydrateMemory(database, memory);
  }

  async softDeleteMemory(memoryId: string) {
    const database = await openDatabase();
    const transaction = database.transaction([MEMORIES_STORE, MEDIA_STORE], "readwrite");
    const done = transactionDone(transaction);
    const memories = transaction.objectStore(MEMORIES_STORE);
    const memory = await requestResult(memories.get(memoryId) as IDBRequest<Memory | undefined>);
    if (!memory || memory.deletedAt) {
      throw new Error("记忆不存在或已删除");
    }
    const deletedAt = new Date().toISOString();
    memories.put({ ...memory, deletedAt, updatedAt: deletedAt });
    const media = transaction.objectStore(MEDIA_STORE);
    for (const mediaId of memory.mediaIds) {
      const stored = await requestResult(media.get(mediaId) as IDBRequest<StoredMedia | undefined>);
      if (stored && !stored.deletedAt) media.put({ ...stored, deletedAt });
    }
    await done;
  }

  async softDeleteMemoryMedia(memoryId: string, mediaId: string) {
    const database = await openDatabase();
    const transaction = database.transaction([MEMORIES_STORE, MEDIA_STORE], "readwrite");
    const done = transactionDone(transaction);
    const memories = transaction.objectStore(MEMORIES_STORE);
    const memory = await requestResult(memories.get(memoryId) as IDBRequest<Memory | undefined>);
    if (!memory || memory.deletedAt || !memory.mediaIds.includes(mediaId)) {
      throw new Error("照片不属于当前记忆");
    }
    const media = transaction.objectStore(MEDIA_STORE);
    const stored = await requestResult(media.get(mediaId) as IDBRequest<StoredMedia | undefined>);
    if (!stored || stored.deletedAt) {
      throw new Error("照片不存在或已移除");
    }
    media.put({ ...stored, deletedAt: new Date().toISOString() });
    await done;
  }

  async listRecentlyDeleted(galaxyId: string) {
    const database = await openDatabase();
    const transaction = database.transaction([MEMORIES_STORE, MEDIA_STORE], "readonly");
    const done = transactionDone(transaction);
    const memories = await requestResult(transaction.objectStore(MEMORIES_STORE).getAll() as IDBRequest<Memory[]>);
    const media = await requestResult(transaction.objectStore(MEDIA_STORE).getAll() as IDBRequest<StoredMedia[]>);
    await done;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const parsedMemories = memories.map((item) => memorySchema.parse(item));
    const memoryById = new Map(parsedMemories.map((item) => [item.id, item]));
    const deletedMemories: RecentlyDeletedItem[] = parsedMemories
      .filter((memory) => memory.spaceId === galaxyId && memory.deletedAt && Date.parse(memory.deletedAt) >= cutoff)
      .map((memory) => ({
        kind: "memory" as const,
        memory,
        deletedAt: memory.deletedAt!,
        mediaCount: media.filter((item) => item.metadata.memoryId === memory.id).length
      }));
    const deletedMedia: RecentlyDeletedItem[] = media
      .filter((item) => item.metadata.memoryId && item.deletedAt && Date.parse(item.deletedAt) >= cutoff)
      .flatMap((item) => {
        const memory = memoryById.get(item.metadata.memoryId);
        if (!memory || memory.spaceId !== galaxyId || memory.deletedAt) return [];
        return [{
          kind: "media" as const,
          memoryId: memory.id,
          memoryTitle: memory.title,
          occurredAt: memory.occurredAt,
          media: mediaAssetSchema.parse(item.metadata),
          deletedAt: item.deletedAt!
        }];
      });
    return [...deletedMemories, ...deletedMedia].toSorted((left, right) => Date.parse(right.deletedAt) - Date.parse(left.deletedAt));
  }

  async restoreMemory(memoryId: string) {
    const database = await openDatabase();
    const transaction = database.transaction([MEMORIES_STORE, MEDIA_STORE], "readwrite");
    const done = transactionDone(transaction);
    const memories = transaction.objectStore(MEMORIES_STORE);
    const memory = await requestResult(memories.get(memoryId) as IDBRequest<Memory | undefined>);
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    if (!memory?.deletedAt || Date.parse(memory.deletedAt) < cutoff) throw new Error("最近删除中找不到这段记忆");
    const deletionBatch = memory.deletedAt;
    memories.put({ ...memory, deletedAt: null, updatedAt: new Date().toISOString() });
    const media = transaction.objectStore(MEDIA_STORE);
    for (const mediaId of memory.mediaIds) {
      const stored = await requestResult(media.get(mediaId) as IDBRequest<StoredMedia | undefined>);
      if (stored?.deletedAt === deletionBatch) media.put({ ...stored, deletedAt: null });
    }
    await done;
  }

  async restoreMemoryMedia(memoryId: string, mediaId: string) {
    const database = await openDatabase();
    const transaction = database.transaction([MEMORIES_STORE, MEDIA_STORE], "readwrite");
    const done = transactionDone(transaction);
    const memory = await requestResult(transaction.objectStore(MEMORIES_STORE).get(memoryId) as IDBRequest<Memory | undefined>);
    const media = transaction.objectStore(MEDIA_STORE);
    const stored = await requestResult(media.get(mediaId) as IDBRequest<StoredMedia | undefined>);
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    if (!memory || memory.deletedAt || !memory.mediaIds.includes(mediaId) || !stored?.deletedAt || Date.parse(stored.deletedAt) < cutoff) {
      throw new Error("最近删除中找不到这张照片");
    }
    media.put({ ...stored, deletedAt: null });
    await done;
  }
}

export const localMemoryRepository: MemoryRepository = new IndexedDbMemoryRepository();
